import {
	type ComponentType,
	forwardRef,
	useState,
	type FormEventHandler,
	type ReactNode,
} from 'react';

import Box, { type BoxProps } from '@liquidweb-dev/undercurrent/Box';
import Collapse from '@liquidweb-dev/undercurrent/Collapse';
import IconButton from '@liquidweb-dev/undercurrent/IconButton';
import CheckIcon from '@liquidweb-dev/undercurrent/icons/lucide/Check';
import PencilIcon from '@liquidweb-dev/undercurrent/icons/lucide/Pencil';
import XIcon from '@liquidweb-dev/undercurrent/icons/lucide/X';
import { outlinedInputClasses } from '@liquidweb-dev/undercurrent/OutlinedInput';
import Skeleton from '@liquidweb-dev/undercurrent/Skeleton';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import TextField, {
	TextFieldProps,
} from '@liquidweb-dev/undercurrent/TextField';
import { type LucideSvgProps } from '@liquidweb-dev/undercurrent/types/svgTypes';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import TypographyOverflow from '@liquidweb-dev/undercurrent/TypographyOverflow';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';
import { useLeftHandLayout } from 'containers/LeftHandLayout';
import PageLayoutAlert, { PageLayoutAlertProps } from './PageLayoutAlert';

const createGridTemplateAreas = (
	hasAttributes: boolean,
	hasActions: boolean,
) => {
	if (hasAttributes && hasActions) {
		return {
			xs: `
      'title title'
      'attributes attributes'
      'actions actions'
      `,
			md: `
      'title actions'
      'attributes attributes'
      `,
		};
	}

	if (!hasAttributes && hasActions) {
		return {
			xs: `
      'title title'
      'actions actions'
      `,
			md: `
      'title actions'
      `,
		};
	}

	if (hasAttributes) {
		return {
			xs: `
      'title title'
      'attributes attributes'
      `,
		};
	}

	return {
		xs: `'title title'`,
	};
};

export interface PageLayoutHeaderProps extends BoxProps {
	actionsSlot?: ReactNode;
	alert?: PageLayoutAlertProps['children'];
	attributesSlot?: ReactNode;
	breadcrumbsSlot?: ReactNode;
	description?: ReactNode;
	Icon?: ComponentType<LucideSvgProps>;
	iconBoxProps?: BoxProps;
	isLoading?: boolean;
	isTitleSaveBusy?: boolean;
	onTitleSave?: (value: string, options: { onSuccess: () => void }) => void;
	title: string;
}

const PageLayoutHeader = forwardRef(
	(
		{
			actionsSlot,
			alert,
			attributesSlot,
			breadcrumbsSlot,
			description,
			Icon,
			iconBoxProps,
			isLoading,
			isTitleSaveBusy,
			onTitleSave,
			title,
			sx,
			...rest
		}: PageLayoutHeaderProps,
		ref,
	) => {
		const { topInnerX, screenSize } = useLeftHandLayout();
		const isSmallScreen = screenSize === 'small';

		const [isEditingTitle, setIsEditingTitle] = useState(false);
		const [newTitle, setNewTitle] = useState(title);

		const handleTitleChange: TextFieldProps['onChange'] = (e) => {
			setNewTitle(e.target.value);
		};

		const handleTitleCancel = () => {
			setIsEditingTitle(false);
			setNewTitle(title);
		};

		const handleTitleSave: FormEventHandler<HTMLFormElement> = (e) => {
			e.preventDefault();
			onTitleSave?.(newTitle, { onSuccess: () => setIsEditingTitle(false) });
		};

		const { sx: iconBoxSx, ...restIconBoxProps } = iconBoxProps || {};

		return (
			<Box
				sx={combineSx(
					{
						paddingY: { xs: 300, md: 600 },
						paddingX: {
							xs: `${topInnerX}px`,
							md: 700,
						},

						transition: 'ease-in-out 0.2s',
					},
					sx,
				)}
				{...rest}
				ref={ref}
			>
				{alert && <PageLayoutAlert>{alert}</PageLayoutAlert>}
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: 100,
						maxWidth: '1200px',
						marginX: 'auto',
						width: '100%',
					}}
				>
					{breadcrumbsSlot && (
						<Stack alignItems="start">{breadcrumbsSlot}</Stack>
					)}
					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns: '1fr auto',
							gridTemplateAreas: createGridTemplateAreas(
								Boolean(attributesSlot),
								Boolean(actionsSlot),
							),
							columnGap: 200,
							rowGap: 150,
						}}
					>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: { xs: 50, md: 100 },
								gridArea: 'title',
								alignSelf: 'center',
								minWidth: 0,
							}}
						>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									minWidth: 0,
									overflow: 'auto',
								}}
							>
								{onTitleSave && (
									<Collapse in={isEditingTitle}>
										<Box
											component="form"
											onSubmit={handleTitleSave}
											sx={{
												alignItems: 'center',
												gap: 50,
												display: isEditingTitle ? 'flex' : 'none',
											}}
										>
											<TextField
												value={newTitle}
												onChange={handleTitleChange}
												size={isSmallScreen ? 'small' : 'large'}
												sx={{
													flexGrow: isEditingTitle ? 1 : 0,
													[`& .${outlinedInputClasses.root}`]: {
														typography: { xs: 'title-xs', md: 'title-lg' },
														fontWeight: 500,

														[`&.${outlinedInputClasses.focused}`]: {
															outline: 'none',
														},
													},
												}}
											/>
											<IconButton
												variant="ghost"
												type="submit"
												disabled={isTitleSaveBusy}
											>
												<CheckIcon
													sx={{
														color: ({ vars }) =>
															vars.palette.uc.success.icon.strong,
													}}
												/>
											</IconButton>
											<IconButton
												variant="ghost"
												onClick={handleTitleCancel}
												sx={{
													color: ({ vars }) =>
														vars.palette.uc.danger.icon.strong,
												}}
											>
												<XIcon />
											</IconButton>
										</Box>
									</Collapse>
								)}

								<Collapse in={!isEditingTitle} sx={{ minWidth: '0px' }}>
									<Box
										sx={{
											display: isEditingTitle ? 'none' : 'flex',
											alignItems: 'center',
											minWidth: 0,
										}}
									>
										{Icon && (
											<Box
												sx={combineSx(
													({ borderRadius }) => ({
														borderRadius: {
															xs: borderRadius.roundedSm,
															md: borderRadius.rounded,
														},
														border: '1px solid',
														borderColor: ({ vars }) =>
															vars.palette.uc.highlight.border.weak,
														backgroundColor: ({ vars }) =>
															vars.palette.uc.highlight.bg.main,
														padding: 100,
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														marginRight: { xs: 100, md: 200 },
													}),
													iconBoxSx,
												)}
												{...restIconBoxProps}
											>
												<Icon
													sx={({ size, vars }) => ({
														height: { xs: size.xS, md: size.md },
														width: { xs: size.xS, md: size.md },
														color: vars.palette.uc.highlight.icon.main,
													})}
												/>
											</Box>
										)}

										<Box
											sx={{
												display: 'flex',
												gap: 50,
												alignItems: 'center',
												minWidth: 0,
											}}
										>
											<TypographyOverflow
												color="uc.text.main"
												sx={{
													typography: {
														xs: 'title-xs',
														md: 'title-lg',
													},
													fontWeight: 500,
													transition: 'ease-in-out 0.2s',
												}}
											>
												{isLoading ? (
													<Skeleton
														variant="rounded"
														width={250}
														sx={(theme) => ({
															height: theme.size.lg,
														})}
													/>
												) : (
													title
												)}
											</TypographyOverflow>

											{onTitleSave && (
												<IconButton
													variant="ghost"
													onClick={() => setIsEditingTitle(true)}
													sx={{ minWidth: (theme) => theme.size.md }}
												>
													<PencilIcon
														sx={{
															height: ({ size }) => size.xS,
															width: ({ size }) => size.xS,
														}}
													/>
												</IconButton>
											)}
										</Box>
									</Box>
								</Collapse>
							</Box>

							{description && (
								<Box>
									<Typography
										component="div"
										sx={{
											typography: { xs: 'body-sm', md: 'body-md' },
											fontWeight: 400,
											maxWidth: '800px',
											color: ({ vars }) => vars.palette.uc.text.weak,
										}}
									>
										{isLoading ? <Skeleton width={200} /> : description}
									</Typography>
								</Box>
							)}
						</Box>

						{attributesSlot && (
							<Box
								sx={{
									display: 'flex',
									gap: 200,
									flexWrap: 'wrap',
									alignItems: 'center',
								}}
							>
								{attributesSlot}
							</Box>
						)}

						{actionsSlot && (
							<Box sx={{ gridArea: 'actions' }}>{actionsSlot}</Box>
						)}
					</Box>
				</Box>
			</Box>
		);
	},
);

PageLayoutHeader.displayName = 'PageLayoutHeader';

export default PageLayoutHeader;
